import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchSuppliers } from "../../core/redux/slices/supplierSlice";
import { getPayables } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// The /api/reports/payables endpoint accepts no query params — it always returns the
// full current snapshot. Supplier/date filtering is therefore applied client-side below.
const Payables = () => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);

  const [supplierId, setSupplierId] = useState("");
  const [asOf, setAsOf] = useState(today());
  const [allData, setAllData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const supplierOptions = [{ value: "", label: "All Suppliers" }, ...suppliers.map((s) => ({ value: s.id, label: s.supplierName }))];

  useEffect(() => { dispatch(fetchSuppliers()); }, [dispatch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async () => {
    setLoading(true);
    try {
      const res = await getPayables();
      const payload = res.data || {};
      setAllData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load payables."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSupplierId("");
    setAsOf(today());
  };

  const selectedSupplierName = supplierOptions.find((o) => o.value === supplierId)?.label;

  const data = useMemo(() => allData.filter((r) => {
    if (supplierId && r.supplierName !== selectedSupplierName) return false;
    if (asOf && r.purchaseDate && r.purchaseDate.split("T")[0] > asOf) return false;
    return true;
  }), [allData, supplierId, asOf, selectedSupplierName]);

  const columns = [
    { title: "Supplier", dataIndex: "supplierName", sorter: (a, b) => (a.supplierName || "").localeCompare(b.supplierName || "") },
    { title: "PO / Ref", dataIndex: "purchaseNo", width: 150 },
    { title: "Purchase Date", dataIndex: "purchaseDate", width: 120, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Total Amount", dataIndex: "totalAmount", align: "right", width: 130, render: fmt },
    { title: "Paid", dataIndex: "paidAmount", align: "right", width: 110, render: (v) => <span className="text-success">{fmt(v)}</span> },
    {
      title: "Balance", dataIndex: "balanceAmount", align: "right", width: 120,
      render: (v) => <span className="text-danger fw-semibold">{fmt(v)}</span>,
      sorter: (a, b) => (b.balanceAmount || 0) - (a.balanceAmount || 0),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Payables Report</h4><h6>Outstanding dues to suppliers</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col" style={{ minWidth: 220, maxWidth: 280 }}>
                <label className="form-label small mb-1">Supplier</label>
                <Select classNamePrefix="react-select" options={supplierOptions}
                  value={supplierOptions.find((o) => o.value && o.value === supplierId) || null}
                  placeholder="All Suppliers"
                  isClearable
                  onChange={(opt) => setSupplierId(opt?.value || "")} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">As of Date</label>
                <input type="date" className="form-control form-control-sm" value={asOf}
                  onChange={(e) => setAsOf(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Payable", value: fmt(summary.totalPayable), color: "danger" },
              { label: "Advances Paid", value: fmt(summary.totalAdvances), color: "info" },
              { label: "Net Payable", value: fmt(summary.netPayable), color: "primary" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
            {data.length > 0 && (
              <div className="mt-2 text-end">
                Total Outstanding: <strong className="text-danger ms-1">
                  {fmt(data.reduce((s, r) => s + (r.balanceAmount || 0), 0))}
                </strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payables;
